import axios, { AxiosError } from "axios";
import { clientCodeHeader, getClientCode } from "../../shared/api/clientApiContext";
import { ApiResponse } from "../../shared/api/types";
import sharedBiClient, {
  apiReports,
  apiTabularEndpoint,
  CancellationToken,
  uiReports,
  uiTabularEndpoint,
} from "../../shared/reporting/api/biClient";
import {
  ApiReportResponse,
  MeasureDescriptor,
  Report,
  ReportResponse,
  ReportUsage,
  TabularDataResponse,
} from "../../shared/reporting/api/biClient.types";
import { ChartResponse, PivotDataResponse, TileDataResponse } from "../../shared/reporting/types/biShared.types.ts";
import { getBackendBaseUrl } from "../../shared/variables";
import {
  BuildChartReportRequest,
  BuildExportRequest,
  BuildPivotReportRequest,
  BuildTabularReportRequest,
  BuildTileReportRequest,
  CashFlowExportRequest,
  ChartOfAccounts,
  ChartOfAccountsDataResponse,
  ChartOfAccountsExportRequest,
  ClientSettings,
  CustomMeasureDescription,
  DrillDownCacheValidationResponse,
  DrillDownConfiguration,
  DrillDownDataRequest,
  DrillDownDataResponse,
  DrillDownInfoResult,
  ExportResponse,
  LedgerBasisRequest,
  LedgerBasisResponse,
  MeasureDataset,
  MetaDataResponse,
  RunExportRequest,
  TabularGroupTotalsResponse,
  UsedByReportsResponse,
} from "../api/biApi.types";
import { dataSetHeader } from "./dataSetApiContext";

const baseUri = getBackendBaseUrl("reporting");

const defaultHeaders = () => {
  return {
    ...clientCodeHeader(),
    ...dataSetHeader(),
  };
};

const loadMeta = async () => {
  const { data } = await axios.get<ApiResponse<MetaDataResponse>>(`${baseUri}/MetaData`, {
    headers: defaultHeaders(),
  });
  return data;
};

const buildTileReport = (
  body: BuildTileReportRequest,
  done: (data: ApiResponse<TileDataResponse>) => void,
  error: (reason: string | undefined) => void
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<TileDataResponse>>(`${baseUri}/UiTile/build`, body, {
      headers: defaultHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const buildPivotReport = (
  body: BuildPivotReportRequest,
  done: (data: ApiResponse<PivotDataResponse>) => void,
  error: (reason: string | undefined) => void
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<PivotDataResponse>>(`${baseUri}/UiPivot/build`, body, {
      headers: defaultHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const buildChartReport = (
  body: BuildChartReportRequest,
  done: (data: ApiResponse<ChartResponse>) => void,
  error: (reason: string | undefined) => void
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<ChartResponse>>(`${baseUri}/UiChart/build`, body, {
      headers: defaultHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<ChartResponse>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const getDrillDownData = async (body: DrillDownDataRequest, signal?: AbortSignal) => {
  const { data } = await axios.post<ApiResponse<DrillDownDataResponse>>(`${baseUri}/DrillDown`, body, {
    headers: defaultHeaders(),
    signal: signal,
  });
  return data;
};

const getDrillDownItem = async (sessionId: string, itemId: string) => {
  const { data } = await axios.get<ApiResponse<DrillDownInfoResult>>(
    `${baseUri}/DrillDown/item?sessionId=${sessionId}&itemId=${itemId}`,
    {
      headers: defaultHeaders(),
    }
  );
  return data;
};

const getDrillDownConfiguration = async () => {
  const { data } = await axios.get<ApiResponse<DrillDownConfiguration>>(`${baseUri}/DrillDown/configuration`, {
    headers: defaultHeaders(),
  });
  return data;
};

const getChartOfAccountsDrillDown = async (sessionId: string, itemId: string) => {
  const { data } = await axios.get<ApiResponse<ChartOfAccountsDataResponse>>(
    `${baseUri}/DrillDown/chartofaccounts?sessionId=${sessionId}&itemId=${itemId}`,
    {
      headers: defaultHeaders(),
    }
  );
  return data;
};

const isDrillDownCacheValid = async (sessionId: string, etag: string) => {
  const { data } = await axios.get<ApiResponse<DrillDownCacheValidationResponse>>(
    `${baseUri}/DrillDown/validatecache?sessionId=${sessionId}&etag=${etag}`,
    {
      headers: defaultHeaders(),
    }
  );
  return data;
};

const requestDrillDownExport = async (body: DrillDownDataRequest) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(`${baseUri}/DrillDown/export`, body, {
    headers: defaultHeaders(),
  });
  return data;
};

const requestCashflowExport = async (body: CashFlowExportRequest) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(`${baseUri}/DrillDown/export/cashflow`, body, {
    headers: defaultHeaders(),
  });
  return data;
};

const requestChartOfAccountsExport = async (body: ChartOfAccountsExportRequest) => {
  const { data } = await axios.post<ApiResponse<ExportResponse>>(`${baseUri}/DrillDown/export/chartofaccounts`, body, {
    headers: defaultHeaders(),
  });
  return data;
};

const checkExportReady = async (token: string) => {
  const { data } = await axios.get<ApiResponse<ExportResponse>>(`${baseUri}/ReportFiles/export?token=${token}`, {
    headers: defaultHeaders(),
  });
  return data;
};

const generateExportDownloadLink = (token: string) => {
  return `${baseUri}/ReportFiles/export/download?token=${token}&clientCode=${getClientCode()}`;
};

const addCustomMeasure = async (measure: CustomMeasureDescription) => {
  const { data } = await axios.post<ApiResponse<MeasureDescriptor>>(`${baseUri}/CustomMeasure`, measure, {
    headers: defaultHeaders(),
  });
  return data;
};

const updateCustomMeasure = async (id: string, measure: CustomMeasureDescription) => {
  const { data } = await axios.put<ApiResponse<MeasureDescriptor>>(`${baseUri}/CustomMeasure/${id}`, measure, {
    headers: defaultHeaders(),
  });
  return data;
};

const deleteCustomMeasure = async (id: string) => {
  const { data } = await axios.delete<ApiResponse<boolean>>(`${baseUri}/CustomMeasure/${id}`, {
    headers: defaultHeaders(),
  });
  return data;
};

const getUsedByReports = async (id: string) => {
  const { data } = await axios.get<ApiResponse<UsedByReportsResponse>>(`${baseUri}/CustomMeasure/${id}/used-by`, {
    headers: defaultHeaders(),
  });
  return data;
};

const getMeasureDataSets = async () => {
  const { data } = await axios.get<ApiResponse<MeasureDataset[]>>(`${baseUri}/CustomMeasure/datasets`, {
    headers: defaultHeaders(),
  });
  return data;
};

const getLedgerBasis = (
  body: LedgerBasisRequest,
  done: (data: ApiResponse<LedgerBasisResponse>) => void,
  error: (reason: string) => void
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<LedgerBasisResponse>>(`${baseUri}/${uiReports}/ledger-basis`, body, {
      headers: defaultHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data))
    .catch((reason: Error) => error(reason.message));
  return { cancel: () => controller.abort() };
};

const getChartOfAccounts = async () => {
  const { data } = await axios.get<ApiResponse<ChartOfAccounts>>(`${baseUri}/ChartOfAccounts`, {
    headers: defaultHeaders(),
  });
  return data;
};

const getClientSettings = async () => {
  const { data } = await axios.get<ApiResponse<ClientSettings>>(`${baseUri}/Clients/settings`, {
    headers: defaultHeaders(),
  });
  return data;
};

const buildTabularReportBase = <
  TBody extends BuildTabularReportRequest,
  TResponse extends TabularDataResponse | ApiReportResponse,
>(
  body: TBody,
  done: (data: ApiResponse<TResponse>) => void,
  error: (reason: string | undefined) => void,
  endpoint: typeof uiTabularEndpoint | typeof apiTabularEndpoint
): CancellationToken => {
  const controller = new AbortController();
  axios
    .post<ApiResponse<TabularDataResponse>>(`${baseUri}/${endpoint}/build`, body, {
      headers: defaultHeaders(),
      signal: controller.signal,
    })
    .then((res) => done(res.data as ApiResponse<TResponse>))
    .catch((reason: Error) => {
      if (reason.message === "canceled") {
        error("canceled");
        return;
      }
      const axiosError = reason as AxiosError<ApiResponse<unknown>>;
      const errorMessage = axiosError.response?.data?.error?.message;
      error(errorMessage);
    });
  return { cancel: () => controller.abort() };
};

const getReportsApi = (usage: ReportUsage) => {
  const reportsEndpoint = usage === "Api" ? apiReports : uiReports;
  const tabularStructuredReportEndpoint = usage === "Api" ? apiTabularEndpoint : uiTabularEndpoint;

  const buildTabularReport = <
    TBody extends BuildTabularReportRequest,
    TResponse extends TabularDataResponse | ApiReportResponse,
  >(
    body: TBody,
    done: (data: ApiResponse<TResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    if (usage === "Api") {
      return buildTabularReportBase(body, done, error, apiTabularEndpoint);
    }
    return buildTabularReportBase(body, done, error, uiTabularEndpoint);
  };

  const getReport = async (reportId: string, mode: "run" | "edit") => {
    const { data } = await axios.get<ApiResponse<Report>>(`${baseUri}/${reportsEndpoint}/${reportId}?mode=${mode}`, {
      headers: defaultHeaders(),
    });
    return data;
  };

  const updateReport = async (report: Report) => {
    const { data } = await axios.put<ApiResponse<ReportResponse>>(`${baseUri}/${reportsEndpoint}`, report, {
      headers: defaultHeaders(),
    });
    return data;
  };

  const addReport = async (report: Report) => {
    const { data } = await axios.post<ApiResponse<ReportResponse>>(`${baseUri}/${reportsEndpoint}`, report, {
      headers: defaultHeaders(),
    });
    return data;
  };

  const calculateTabularGroupTotalsData = (
    body: BuildTabularReportRequest,
    done: (data: ApiResponse<TabularGroupTotalsResponse>) => void,
    error: (reason: string | undefined) => void
  ): CancellationToken => {
    const controller = new AbortController();
    axios
      .post<ApiResponse<TabularGroupTotalsResponse>>(
        `${baseUri}/${tabularStructuredReportEndpoint}/groupTotals`,
        body,
        {
          headers: defaultHeaders(),
          signal: controller.signal,
        }
      )
      .then((res) => done(res.data))
      .catch((reason: Error) => {
        if (reason.message === "canceled") {
          error("canceled");
          return;
        }
        const axiosError = reason as AxiosError<ApiResponse<unknown>>;
        const errorMessage = axiosError.response?.data?.error?.message;
        error(errorMessage);
      });
    return { cancel: () => controller.abort() };
  };

  const requestRunTabularExport = async (body: RunExportRequest) => {
    const { data } = await axios.post<ApiResponse<ExportResponse>>(
      `${baseUri}/${tabularStructuredReportEndpoint}/runExport`,
      body,
      {
        headers: defaultHeaders(),
      }
    );
    return data;
  };

  const requestBuildTabularExport = async (body: BuildExportRequest) => {
    const { data } = await axios.post<ApiResponse<ExportResponse>>(
      `${baseUri}/${tabularStructuredReportEndpoint}/buildExport`,
      body,
      {
        headers: defaultHeaders(),
      }
    );
    return data;
  };

  const requestBuildPivotExport = async (body: BuildExportRequest) => {
    const { data } = await axios.post<ApiResponse<ExportResponse>>(`${baseUri}/UiPivot/buildExport`, body, {
      headers: defaultHeaders(),
    });
    return data;
  };

  const requestRunPivotExport = async (body: RunExportRequest) => {
    const { data } = await axios.post<ApiResponse<ExportResponse>>(`${baseUri}/UiPivot/runExport`, body, {
      headers: defaultHeaders(),
    });
    return data;
  };

  return {
    getReport,
    updateReport,
    addReport,
    calculateTabularGroupTotalsData,
    buildTabularReport,
    requestRunTabularExport,
    requestBuildTabularExport,
    requestBuildPivotExport,
    requestRunPivotExport,
  };
};

const biClient = {
  loadMeta,
  buildTileReport,
  buildPivotReport,
  buildChartReport,
  getDrillDownData,
  getDrillDownItem,
  getDrillDownConfiguration,
  getChartOfAccountsDrillDown,
  isDrillDownCacheValid,
  requestDrillDownExport,
  requestCashflowExport,
  requestChartOfAccountsExport,
  checkExportReady,
  generateExportDownloadLink,
  addCustomMeasure,
  updateCustomMeasure,
  deleteCustomMeasure,
  getUsedByReports,
  getMeasureDataSets,
  getLedgerBasis,
  getChartOfAccounts,
  getClientSettings,
  uiReports: {
    getReportAuthorizationInfo: sharedBiClient.uiReports.getReportAuthorizationInfo,
    validateName: sharedBiClient.uiReports.validateReportName,
    renameReport: sharedBiClient.uiReports.renameReport,
    deleteReports: sharedBiClient.uiReports.deleteReports,
    ...getReportsApi("Ui"),
    runTabularReport: sharedBiClient.uiReports.runTabularReport,
    ...sharedBiClient.uiReportGroup,
  },
  apiReports: {
    getReportAuthorizationInfo: sharedBiClient.apiReports.getReportAuthorizationInfo,
    validateName: sharedBiClient.apiReports.validateReportName,
    renameReport: sharedBiClient.apiReports.renameReport,
    deleteReports: sharedBiClient.apiReports.deleteReports,
    ...getReportsApi("Api"),
    runReport: sharedBiClient.apiReports.runTabularReport,
    ...sharedBiClient.apiReportGroup,
  },
};

export default biClient;
